import { useRoute } from '@react-navigation/native';
import { useFormikContext } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { SelectModal } from '@components/FormModals/SelectModal';
import { useListTeamsFromQuery } from '@hooks/useListTeamsFromQuery';
import useSelectedMembers from '@hooks/useSelectedMembers';
import { useTeamBillingFromQuery } from '@hooks/useTeamBillingFromQuery';
import { useAppNavigation } from '@navigation/useAppNavigation';
import { isUserTrialEligible } from '@utils/teams/paywall';

interface TeamSelectProps {
  isBottomSheet?: boolean;
  onSelectFinish?: () => void;
}
export const TeamSelect = ({
  isBottomSheet,
  onSelectFinish,
}: TeamSelectProps) => {
  const { t } = useTranslation('models');
  const { teams: teamsFromQuery } = useListTeamsFromQuery({});
  const { setFieldValue, getFieldProps, touched, setTouched } =
    useFormikContext();
  const { name: fieldName, value } = getFieldProps('teamId');
  const navigation = useAppNavigation();
  const { setShowTeamSelect } = useSelectedMembers();
  const route = useRoute();
  const disablePersonalFromParams = (
    route.params as { disablePersonalProjects?: boolean } | undefined
  )?.disablePersonalProjects;

  // TA-9872: subscriptionDetails was split out of listTeams — merge it back
  // from the getTeamBilling query for the trial-eligibility check.
  const { billingByTeamId } = useTeamBillingFromQuery();
  const teamsWithBilling = teamsFromQuery.map((item) => ({
    ...item,
    subscriptionDetails: billingByTeamId[item.id]?.subscriptionDetails,
  }));

  // Billing loads asynchronously (TA-9872). isUserTrialEligible infers
  // subscription history from subscriptionDetails across all teams, so while
  // billing is empty every team looks history-free and a previously-subscribed
  // user would be wrongly marked eligible. Treat "billing not loaded yet" as
  // unknown → ineligible (the safe default; re-evaluates once billing arrives).
  const billingReady =
    teamsFromQuery.length === 0 || Object.keys(billingByTeamId).length > 0;

  // Align with web: only disable personal team when at limit AND not trial-eligible
  // (trial-eligible users can select personal team to trigger paywall and start trial)
  const personalProjectTeam = teamsWithBilling.filter((i) => i.personal);
  const personalTeam = personalProjectTeam?.[0];
  const hasReachedLimit = personalTeam?.hasReachedMaxProjects ?? false;
  const trialEligible =
    billingReady && isUserTrialEligible(teamsWithBilling, personalTeam);
  const disablePersonalProjects =
    disablePersonalFromParams ?? (hasReachedLimit && !trialEligible);

  return (
    <SelectModal
      title='Teams'
      list={teamsFromQuery.map(({ id, name }) => ({ id, name }))}
      onClose={() => {
        setShowTeamSelect(false);
        navigation.goBack();
      }}
      setSelected={(id) => {
        setFieldValue(fieldName, id);
        setTouched({ ...touched, [fieldName]: true });
        setShowTeamSelect(false);
        onSelectFinish ? onSelectFinish() : navigation.goBack();
      }}
      value={value}
      singleSelect
      disablePersonalTeam={disablePersonalProjects}
      personalTeamId={teamsFromQuery?.find((team) => team?.personal)?.id}
      isBottomSheet={isBottomSheet}
      // TA-10950 #22: team search box follows the standard spec (white bg,
      // Grey02/black focus border, 48px height, hover on the X clear icon).
      searchInputVariant='standard'
      // TA-10950 #23: selecting a team already closes the modal (setSelected
      // above calls goBack), so the footer Done button was a redundant
      // second close action - the header X is now the single close/complete
      // control.
      hideDoneButton
      // TA-10950 #25: match Project list's hover - underline the team name
      // only (no grey01 row background, icon untouched).
      itemHoverVariant='underline'
      emptyContentMessage={t('resourceNotFound.message', {
        resource: 'team',
        resourcePlural: 'teams',
      })}
    />
  );
};
